import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col } from "reactstrap";
import { useDropzone } from "react-dropzone";
import { imageBaseApi } from "../../../utilis/imageHelper";

const FileUpload = ({
  onUpload,
  selectedFiles = [], // new files (File objects)
  existingFiles = [], // existing media from server
  onRemoveNew,
  onRemoveExisting,
}) => {
  console.log("🚀 ~ FileUpload ~ existingFiles:", existingFiles)
  const handleAcceptedFiles = useCallback(
    (acceptedFiles) => {
      const filesWithPreview = acceptedFiles.map((file) => {
        const preview = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null;

        const formattedSize =
          file.size < 1024
            ? `${file.size} bytes`
            : file.size < 1048576
            ? `${(file.size / 1024).toFixed(2)} KB`
            : `${(file.size / 1048576).toFixed(2)} MB`;

        return {
          file,
          preview,
          name: file.name,
          formattedSize,
        };
      });

      onUpload(
        filesWithPreview.map((f) => ({
          file: f.file,
          fileName: f.name,
          mimeType: f.file.type,
          size: f.file.size,
        }))
      );
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleAcceptedFiles,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "application/pdf": [".pdf"],
      "text/*": [".txt", ".csv"],
    },
    multiple: true, // ✅ allow multiple files
  });

  const formatSize = (size) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1048576).toFixed(2)} MB`;
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [selectedFiles]);

  return (
    <div className="dropzone dz-clickable pt-3">
      <div
        className="dz-message needsclick mb-5 p-3"
        {...getRootProps()}
        style={{
          borderRadius: "10px",
          textAlign: "center",
          background: isDragActive ? "#f0f8ff" : "#fafafa",
          cursor: "pointer",
        }}
      >
        <i className="align-item-center display-4 text-muted ri-upload-cloud-2-fill" />
        <input {...getInputProps()} />
        <h6 className="fs-15 fw-semi-bold pt-3">
          {isDragActive ? "Drop files here…" : "Drop files here or "}
          <span className="text-primary">click to upload</span>.
        </h6>
        <small className="text-muted d-block mt-2">
          Supported files: Images, PDFs, Text files...
        </small>
      </div>

      {/* Only show existing files if editing */}
      {existingFiles.length > 0 && (
        <>
          {existingFiles.map((file, index) => (
            <div
              key={`exist-${file.id}`}
              className="card mb-2 shadow-none border"
            >
              <div className="card-body p-2">
                <div className="d-flex align-items-center">
                  {file.mimeType?.startsWith("image/") ? (
                    <img
                      height="80"
                      className="avatar-sm rounded bg-light object-fit-cover me-3"
                      alt={file.name}
                      src={
                        file.url
                          ? `${imageBaseApi}${file.url}`
                          : "/path/to/fallback.png"
                      }
                    />
                  ) : (
                    <div
                      className="bg-light rounded d-flex align-items-center justify-content-center me-3"
                      style={{ width: 80, height: 80 }}
                    >
                      {file.name?.split(".").pop()?.toUpperCase()}
                    </div>
                  )}

                  <div className="flex-grow-1">
                    <div className="fw-bold">{file.name}</div>
                    <small className="text-muted">
                      {formatSize(file.size)} • Existing
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn btn-link text-danger p-0 ms-2"
                    onClick={() => onRemoveExisting(index)}
                  >
                    <i className="ri-delete-bin-2-line fs-4"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Newly selected files */}
      {selectedFiles.map((file, index) => (
        <div
          key={`new-${index}`}
          className="card mb-2 shadow-none border dz-success"
        >
          <div className="card-body p-2">
            <div className="d-flex align-items-center">
              {file.type.startsWith("image/") ? (
                <img
                  height="80"
                  className="avatar-sm rounded bg-light object-fit-cover me-3"
                  alt={file.name}
                  src={URL.createObjectURL(file)}
                />
              ) : (
                <div
                  className="bg-light rounded d-flex align-items-center justify-content-center me-3"
                  style={{ width: 80, height: 80 }}
                >
                  {file.name.split(".").pop().toUpperCase()}
                </div>
              )}

              <div className="flex-grow-1">
                <div className="fw-bold text-truncate">{file.name}</div>
                <small className="text-muted">
                  {formatSize(file.size)} • New
                </small>
              </div>

              <button
                type="button"
                className="btn btn-link text-danger p-0 ms-2"
                onClick={() => onRemoveNew(index)}
              >
                <i className="ri-delete-bin-2-line fs-4"></i>
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileUpload;
